// Adapted from ifc-engine-core/demo/viewer-core/src/mesh-source.ts and aec-project-controls's
// src/domain/ifcModelImport.ts (same author, same license -- see
// src/vendor/ifc-mesh-wasm-0.4.1/NOTICE.md for the exact pin). Trimmed and reshaped for this app's
// own architecture: this file only decodes chunk bytes into per-GUID geometry and merges that
// geometry into an already-built AnonymousFixture; it never builds Three.js objects (that stays in
// src/ui/scene.ts) and never invents, drops, or reorders a FixtureElement.

import type { AnonymousFixture, FixtureElement, MeshGeometry } from "./types";
import { VERTEX_STRIDE_BYTES, type ManifestChunk, type MeshBufferHeader } from "./ifc-mesh-source";

export interface DecodedMeshChunk {
  readonly manifestChunk: ManifestChunk;
  readonly header: MeshBufferHeader;
  readonly blob: Uint8Array;
}

export type MeshChunkGeometry = Omit<MeshGeometry, "kind">;

/**
 * Splits one chunk's triangles by `instanceIndex` (the first corner of each triangle decides -- the
 * same convention ifc-engine-core's own hierarchy index uses) so each IFC element (identified by its
 * GlobalId, from the chunk's `guid_table`) gets its own geometry.
 *
 * Named test: "splits one chunk's triangles by instanceIndex into per-GUID geometry".
 */
export function decodeChunkGeometryByGuid(chunk: DecodedMeshChunk): ReadonlyMap<string, MeshChunkGeometry> {
  const { header, blob } = chunk;
  const chunkHeader = header.chunks[0];
  if (chunkHeader === undefined) return new Map();
  const guidTable = header.guid_table;

  const vertexCount = chunkHeader.vertices.count;
  const positions = new Float32Array(vertexCount * 3);
  const instanceIndices = new Uint32Array(vertexCount);
  const [originX, originY, originZ] = chunkHeader.quant_origin_mm;
  const scale = chunkHeader.quant_scale_mm;
  const vertexView = new DataView(blob.buffer, blob.byteOffset + chunkHeader.vertices.byte_offset);
  for (let i = 0; i < vertexCount; i++) {
    const base = i * VERTEX_STRIDE_BYTES;
    const sourceX = (originX + vertexView.getUint16(base, true) * scale) / 1_000;
    const sourceY = (originY + vertexView.getUint16(base + 2, true) * scale) / 1_000;
    const sourceZ = (originZ + vertexView.getUint16(base + 4, true) * scale) / 1_000;
    // ifc-mesh-wasm's world space is Z-up, matching IFC itself -- aec-project-controls's own
    // viewport confirms this by setting `camera.up.set(0, 0, 1)` rather than transforming vertices
    // (see IfcModelViewport.tsx). Workbench's scene is Y-up throughout (its box-geometry pipeline
    // already places box height along Y), so a straight X/Y/Z copy here rendered every mesh record
    // rotated 90° onto its side. Converting at decode time (Y <- source Z "up", Z <- -source Y,
    // the standard right-handed Z-up-to-Y-up swap) keeps this file's own output already Y-up, so
    // src/ui/scene.ts's "identity transform" doc comment for mesh geometry stays literally true.
    positions[i * 3] = sourceX;
    positions[i * 3 + 1] = sourceZ;
    positions[i * 3 + 2] = -sourceY;
    instanceIndices[i] = vertexView.getUint32(base + 8, true);
  }

  const indexView = new DataView(blob.buffer, blob.byteOffset + chunkHeader.indices.byte_offset);
  const indexCount = chunkHeader.indices.count;
  const indices = new Uint32Array(indexCount);
  for (let i = 0; i < indexCount; i++) {
    indices[i] = chunkHeader.indices.index_type === "u16" ? indexView.getUint16(i * 2, true) : indexView.getUint32(i * 4, true);
  }

  const trianglesByInstance = new Map<number, number[]>();
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const a = indices[t];
    const b = indices[t + 1];
    const c = indices[t + 2];
    const instance = instanceIndices[a];
    const existing = trianglesByInstance.get(instance);
    if (existing === undefined) trianglesByInstance.set(instance, [a, b, c]);
    else existing.push(a, b, c);
  }

  const result = new Map<string, MeshChunkGeometry>();
  for (const [instance, vertexIndices] of trianglesByInstance) {
    const guid = guidTable[instance];
    // No stable IFC GlobalId for this instance: there is no FixtureElement this geometry could ever
    // be matched to, so it is dropped here rather than kept unreachable.
    if (guid === undefined || guid === "") continue;
    const uniqueVertices = [...new Set(vertexIndices)];
    const remap = new Map(uniqueVertices.map((vertexIndex, position) => [vertexIndex, position]));
    const geomPositions = new Float32Array(uniqueVertices.length * 3);
    uniqueVertices.forEach((vertexIndex, position) => {
      geomPositions[position * 3] = positions[vertexIndex * 3];
      geomPositions[position * 3 + 1] = positions[vertexIndex * 3 + 1];
      geomPositions[position * 3 + 2] = positions[vertexIndex * 3 + 2];
    });
    const triangleIndices = new Uint32Array(vertexIndices.map((vertexIndex) => remap.get(vertexIndex)!));
    result.set(guid, { vertexPositions: geomPositions, triangleIndices, triangleCount: triangleIndices.length / 3 });
  }
  return result;
}

export interface MeshExtractionGeometry {
  readonly geometryByGuid: ReadonlyMap<string, MeshChunkGeometry>;
  readonly rawTriangleCount: number;
  readonly retainedTriangleCount: number;
  readonly overwrittenGuidCount: number;
  readonly chunkCount: number;
}

/**
 * Merges every decoded chunk's per-GUID geometry into one model-wide map. A GUID split across
 * multiple chunks (a storey sub-split past 65,535 vertices) keeps only its last chunk's geometry --
 * the known `ifc-mesh-wasm` limitation this app inherits (see NOTICE.md). `overwrittenGuidCount`
 * makes that loss visible instead of silent; `rawTriangleCount` (every chunk's own reported count)
 * vs. `retainedTriangleCount` (what the merged map actually keeps) gives the same fact numerically.
 *
 * Named test: "accumulates chunks across storeys, keeping the last chunk for a repeated GUID".
 */
export function accumulateMeshGeometry(chunks: readonly DecodedMeshChunk[]): MeshExtractionGeometry {
  const geometryByGuid = new Map<string, MeshChunkGeometry>();
  let rawTriangleCount = 0;
  let overwrittenGuidCount = 0;
  for (const chunk of chunks) {
    rawTriangleCount += chunk.header.chunks[0]?.triangle_count ?? 0;
    for (const [guid, geometry] of decodeChunkGeometryByGuid(chunk)) {
      if (geometryByGuid.has(guid)) overwrittenGuidCount += 1;
      geometryByGuid.set(guid, geometry);
    }
  }
  let retainedTriangleCount = 0;
  for (const geometry of geometryByGuid.values()) retainedTriangleCount += geometry.triangleCount;
  return { geometryByGuid, rawTriangleCount, retainedTriangleCount, overwrittenGuidCount, chunkCount: chunks.length };
}

export interface MeshFixtureMergeAccounting {
  readonly meshMatched: number;
  readonly boxFallback: number;
  readonly unrenderedUnchanged: number;
  readonly producerSkippedUnchanged: number;
  readonly meshOnlySynthesized: number;
}

export interface MeshFixtureMergeResult {
  readonly fixture: AnonymousFixture;
  readonly accounting: MeshFixtureMergeAccounting;
}

/**
 * Prefers tessellated mesh geometry over the existing plan-derived box for any already-retained
 * record whose `GlobalId` property the mesh engine could also resolve; every other record keeps its
 * existing box geometry, or stays unrendered, exactly as the plan projection produced it. This never
 * reorders a `FixtureElement`, and never touches an existing element's `editable` /
 * `unsupportedReason` (the D2 proposal pipeline's own gates) -- only `.geometry` on an
 * already-retained record can change, so `meshMatched + boxFallback + unrenderedUnchanged +
 * producerSkippedUnchanged` always equals the *input* `fixture.elements.length` exactly once (the
 * output fixture can be longer -- see `meshOnlySynthesized` below).
 *
 * A `type: "Skipped"` record (the plan importer's own diagnostic entry for an entity it could not
 * reconstruct at all -- see skippedElement() in local-ifc-projection.ts) is still checked against
 * `geometryByGuid` by its GlobalId, the same as every other record: the plan importer failing to
 * reconstruct an entity does not mean the separate mesh engine also failed for it, and on a real
 * building the two do disagree often (mesh engine covers many entities the plan importer only
 * skipped). `producerSkippedUnchanged` now counts a Skipped record only when the mesh engine also
 * had nothing for it, not every Skipped record unconditionally -- silently capping every skipped
 * entity out of ever being renderable, even when real mesh geometry existed for it, defeated the
 * purpose of adding a second, independent geometry source.
 *
 * A GUID present in `geometryByGuid` but matching no `FixtureElement.properties.GlobalId` at all
 * -- not even a "Skipped" diagnostic entry -- means the plan importer never reported this entity
 * under any collection or skip reason; it is entirely absent from `raw.plan` and `raw.report`
 * alike (verified against a real IFC source, not assumed). Silently discarding such a GUID's mesh
 * geometry would be exactly the silent loss this app's own No-Silent-Loss discipline exists to
 * prevent, so this merge synthesizes one minimal, honestly-labeled `FixtureElement` per orphan
 * GUID instead (`meshOnlySynthesized` in the accounting) -- the only place in this pipeline that
 * adds a `FixtureElement` the plan projection did not itself produce, and only because the
 * alternative is a record with real geometry that no part of this app could ever show or count.
 *
 * Named test: "replaces box geometry with a matching mesh and falls back to box otherwise, exactly once".
 */
export function mergeMeshGeometryIntoFixture(
  fixture: AnonymousFixture,
  geometryByGuid: ReadonlyMap<string, MeshChunkGeometry>
): MeshFixtureMergeResult {
  let meshMatched = 0;
  let boxFallback = 0;
  let unrenderedUnchanged = 0;
  let producerSkippedUnchanged = 0;
  const matchedGuids = new Set<string>();
  const elements: FixtureElement[] = fixture.elements.map((element) => {
    const globalId = typeof element.properties.GlobalId === "string" && element.properties.GlobalId !== "" ? element.properties.GlobalId : null;
    const meshGeometry = globalId === null ? undefined : geometryByGuid.get(globalId);
    if (meshGeometry === undefined) {
      if (element.type === "Skipped") producerSkippedUnchanged += 1;
      else if (element.geometry !== null) boxFallback += 1;
      else unrenderedUnchanged += 1;
      return element;
    }
    meshMatched += 1;
    matchedGuids.add(globalId!);
    return { ...element, geometry: { kind: "mesh", ...meshGeometry } };
  });
  let meshOnlySynthesized = 0;
  for (const [guid, meshGeometry] of geometryByGuid) {
    if (matchedGuids.has(guid)) continue;
    meshOnlySynthesized += 1;
    elements.push({
      id: `ifc-mesh-only-${guid}`,
      label: `Mesh-only record ${guid}`,
      type: "MeshOnly",
      properties: { GlobalId: guid },
      quantities: {},
      classification: null,
      geometry: { kind: "mesh", ...meshGeometry },
      editable: false,
      unsupportedReason: "This record exists only in the tessellated mesh extraction; the plan importer did not report it under any collection or skip diagnostic.",
      unknown: {}
    });
  }
  return {
    fixture: { ...fixture, elements },
    accounting: { meshMatched, boxFallback, unrenderedUnchanged, producerSkippedUnchanged, meshOnlySynthesized }
  };
}
